import React, { useEffect, useState } from 'react';
import { supabase } from '../utils/supabase';

const EMAILJS_SERVICE   = 'service_nw27jqo';
const TEMPLATE_PATIENT  = 'template_o7p2u3p';
const TEMPLATE_ADMIN    = 'template_1tyhs28';
const EMAILJS_PUBLIC_KEY = 'ugdcV2bsdpwdC26jF';

// Poll until the CDN injects window.emailjs, or give up after `timeout` ms.
const waitForEmailJS = (timeout = 10000) =>
  new Promise((resolve) => {
    if (window.emailjs) { resolve(true); return; }
    let elapsed = 0;
    const id = setInterval(() => {
      elapsed += 100;
      if (window.emailjs) { clearInterval(id); resolve(true); }
      else if (elapsed >= timeout) { clearInterval(id); resolve(false); }
    }, 100);
  });

const SERVICE_LABELS = {
  'home':      'Home Visit — Standard',
  'urgent':    'Home Visit — Urgent (Same-Day)',
  'corporate': 'Aged Care / Corporate',
  'ndis':      'NDIS Visit',
};

// HH:MM stored in DB → human-readable window for emails + UI
const TIME_DISPLAY = {
  '07:00': '7:00 AM – 9:00 AM',
  '09:00': '9:00 AM – 11:00 AM',
  '11:00': '11:00 AM – 1:00 PM',
  '13:00': '1:00 PM – 3:00 PM',
  '15:00': '3:00 PM – 5:00 PM',
};

// Mirrors BookingForm.jsx — local time parse avoids UTC off-by-one in AEST
const formatDate = (dateStr) => {
  if (!dateStr) return 'TBC';
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day)
    .toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
};

export default function BookingConfirmed() {
  const [booking,   setBooking]   = useState(null);
  const [pageState, setPageState] = useState('loading'); // 'loading' | 'confirmed' | 'not-found'

  // No separate init effect needed — waitForEmailJS() handles CDN timing inside run().

  useEffect(() => {
    const run = async () => {
      const ref = new URLSearchParams(window.location.search).get('ref');

      if (!ref) {
        setPageState('not-found');
        return;
      }

      // 1. Fetch booking row
      const { data, error } = await supabase
        .from('patient_bookings')
        .select('*')
        .eq('booking_ref', ref)
        .single();

      if (error || !data) {
        console.error('[BookingConfirmed] Could not fetch booking:', error?.message, '| ref:', ref);
        setPageState('not-found');
        return;
      }

      // Render the confirmation UI immediately — nothing below blocks the user
      setBooking(data);
      setPageState('confirmed');

      // Idempotency guard: 'paid' means this page already ran on a prior load
      if (data.payment_status === 'paid') return;

      // a) Supabase update: payment_status = 'paid', status = 'confirmed'
      try {
        await supabase
          .from('patient_bookings')
          .update({ payment_status: 'paid', status: 'confirmed' })
          .eq('booking_ref', ref);
      } catch (updateErr) {
        console.error('[BookingConfirmed] Supabase update failed:', updateErr);
        // Continue to attempt emails even if the update failed
      }

      // b) + c) Patient confirmation + admin notification emails.
      // Wait for the CDN <script> to inject window.emailjs (it loads after React).
      const ejsReady = await waitForEmailJS();
      if (!ejsReady) {
        console.error('[BookingConfirmed] EmailJS CDN did not load within 10 s — emails skipped');
        return;
      }

      // v4 init() expects an options object — passing a bare string is silently
      // ignored in @emailjs/browser@4, so use {publicKey} explicitly.
      window.emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });

      const fullName      = `${data.first_name || ''} ${data.last_name || ''}`.trim();
      const serviceName   = SERVICE_LABELS[data.service_type] || data.service_type;
      const formattedDate = formatDate(data.scheduled_date);
      const formattedTime = TIME_DISPLAY[data.scheduled_time] || data.scheduled_time || 'TBC';

      const patientParams = {
        to_name:        fullName,
        patient_email:  data.email,
        booking_ref:    data.booking_ref,
        service_type:   serviceName,
        scheduled_date: formattedDate,
        scheduled_time: formattedTime,
        address:        data.address,
        amount:         `$${data.amount_charged}`,
        payment_method: 'Paid online',
      };

      const adminParams = {
        to_email:          'hello@nurahealth.com.au',
        patient_name:      fullName,
        patient_email:     data.email,
        patient_phone:     data.phone,
        booking_ref:       data.booking_ref,
        service_type:      serviceName,
        scheduled_date:    formattedDate,
        scheduled_time:    formattedTime,
        address:           data.address,
        amount:            `$${data.amount_charged}`,
        payment_method:    'Paid online',
        referral_uploaded: data.referral_uploaded ? 'Yes — uploaded' : 'No — patient to bring on the day',
        notes:             data.special_notes || 'No special notes',
      };

      const results = await Promise.allSettled([
        window.emailjs.send(EMAILJS_SERVICE, TEMPLATE_PATIENT, patientParams, { publicKey: EMAILJS_PUBLIC_KEY }),
        window.emailjs.send(EMAILJS_SERVICE, TEMPLATE_ADMIN,   adminParams,   { publicKey: EMAILJS_PUBLIC_KEY }),
      ]);

      if (results[0].status === 'rejected') {
        console.error('[BookingConfirmed] Patient email failed:', results[0].reason);
      }
      if (results[1].status === 'rejected') {
        console.error('[BookingConfirmed] Admin email failed:', results[1].reason);
      }
    };

    run();
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────

  const wrap = {
    minHeight: '100vh',
    background: '#0D1117',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  };

  if (pageState === 'loading') {
    return (
      <div style={wrap}>
        <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.95rem' }}>Loading…</p>
      </div>
    );
  }

  if (pageState === 'not-found') {
    return (
      <div style={wrap}>
        <div style={{ textAlign: 'center', maxWidth: 480 }}>
          <h1 style={{ color: '#80E5CB', fontSize: '2rem', fontWeight: 700, marginBottom: '1rem' }}>
            Thank you for your booking
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: '2rem' }}>
            Your payment was received. We'll be in touch shortly with your confirmation details.
            If you have questions call us on{' '}
            <a href="tel:0420424290" style={{ color: '#80E5CB' }}>0420 424 290</a>.
          </p>
          <a href="/"
            style={{
              display: 'inline-block',
              padding: '0.75rem 2rem',
              background: '#80E5CB',
              color: '#00001E',
              borderRadius: '0.75rem',
              fontWeight: 700,
              fontSize: '0.95rem',
              textDecoration: 'none',
            }}
          >
            Back to Home
          </a>
        </div>
      </div>
    );
  }

  // ── confirmed state ────────────────────────────────────────────────────────
  const fullName      = `${booking.first_name || ''} ${booking.last_name || ''}`.trim();
  const serviceName   = SERVICE_LABELS[booking.service_type] || booking.service_type;
  const formattedDate = formatDate(booking.scheduled_date);
  const formattedTime = TIME_DISPLAY[booking.scheduled_time] || booking.scheduled_time || 'TBC';

  const row = (Icon, label, value) => (
    <div key={label} style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.85rem',
      padding: '0.75rem 0',
      borderBottom: '1px solid rgba(255,255,255,0.07)',
    }}>
      <span style={{ color: '#80E5CB', marginTop: 2, flexShrink: 0 }}>{Icon}</span>
      <span style={{ color: '#9BA5C2', fontSize: '0.85rem', width: 72, flexShrink: 0 }}>{label}</span>
      <span style={{ color: '#fff', fontSize: '0.85rem' }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={wrap}>
      <div style={{
        background: '#161B27',
        border: '1px solid rgba(128,229,203,0.18)',
        borderRadius: '1.25rem',
        padding: '2.5rem 2rem',
        maxWidth: 520,
        width: '100%',
        textAlign: 'center',
      }}>

        {/* Check circle */}
        <div style={{
          width: 68, height: 68,
          borderRadius: '50%',
          background: 'rgba(128,229,203,0.12)',
          border: '2px solid rgba(128,229,203,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 1.5rem',
          fontSize: '1.6rem',
          color: '#80E5CB',
        }}>
          ✓
        </div>

        <h1 style={{
          color: '#fff',
          fontSize: '2rem',
          fontWeight: 800,
          marginBottom: '0.5rem',
          letterSpacing: '-0.02em',
        }}>
          Booking Received!
        </h1>

        <p style={{ color: 'rgba(255,255,255,0.55)', marginBottom: '0.4rem', fontSize: '0.95rem' }}>
          A confirmation has been sent to{' '}
          <strong style={{ color: '#80E5CB' }}>{booking.email}</strong>
        </p>
        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', marginBottom: '1.75rem' }}>
          Reference: <span style={{ color: '#80E5CB', fontWeight: 600 }}>{booking.booking_ref}</span>
        </p>

        {/* Detail rows */}
        <div style={{
          background: 'rgba(0,0,0,0.25)',
          borderRadius: '0.875rem',
          padding: '0 1.25rem',
          textAlign: 'left',
          marginBottom: '1.75rem',
        }}>
          {row('👤', 'Name',    fullName)}
          {row('✉️',  'Email',   booking.email)}
          {row('📞', 'Phone',   booking.phone)}
          {row('📍', 'Address', booking.address)}
          {row('📅', 'Date',    formattedDate)}
          {row('🕐', 'Time',    formattedTime)}
          {row('🩸', 'Service', serviceName)}
        </div>

        <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.78rem', lineHeight: 1.6, marginBottom: '1.5rem' }}>
          Have your referral/pathology form ready and be available at your address during the appointment window.
          Questions?{' '}
          <a href="tel:0420424290" style={{ color: '#80E5CB' }}>0420 424 290</a>
        </p>

        <a
          href="/booking"
          style={{
            display: 'block',
            padding: '0.875rem',
            background: 'rgba(128,229,203,0.08)',
            border: '1px solid rgba(128,229,203,0.3)',
            color: '#80E5CB',
            borderRadius: '0.75rem',
            fontWeight: 600,
            fontSize: '0.95rem',
            textDecoration: 'none',
            marginBottom: '0.75rem',
          }}
        >
          Book Another Collection
        </a>

        <a
          href="/"
          style={{
            display: 'block',
            padding: '0.875rem',
            background: '#80E5CB',
            color: '#00001E',
            borderRadius: '0.75rem',
            fontWeight: 700,
            fontSize: '0.95rem',
            textDecoration: 'none',
          }}
        >
          Back to Home
        </a>

      </div>
    </div>
  );
}
